// ═══════════════════════════════════════════════════════════════
// POLLEN CARD — Recent Gifts
// Path: src/components/dashboard/pollen/PollenRecentGifts.jsx
//
// "Someone just gave. Go say thank you."
// Absorbs the old RecentGifts widget with the pollen lens:
// these are flowers that just released pollen — go collect it.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import { Gift, X, Sprout } from "lucide-react";
import { useApp } from "../../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../../constants/appConfig";
import { supabase } from "../../../lib/supabase";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const fmtDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T12:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const PollenRecentGifts = ({ onClose }) => {
  const { palette, navigate, dataVersion, members, cultivationQueue } = useApp();
  const [gifts, setGifts] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data, error } = await supabase
          .from("gifts")
          .select(
            "id, amount, gift_date, member_id, members (id, first_name, last_name, credentialed_full_name)"
          )
          .order("gift_date", { ascending: false })
          .limit(8);

        if (!error && data) setGifts(data);
      } catch (err) {
        // silent
      }
    };
    load();
  }, [dataVersion]);

  const getDonorName = (gift) => {
    const member = members.find((m) => m.id === gift.member_id);
    if (!member) return "—";
    return (
      member.credentialedFullName ||
      `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
      "—"
    );
  };

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 10,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "12px 16px",
          borderBottom: "none",
          background: palette.background,
          borderRadius: "10px 10px 0 0",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Gift size={ICON_SIZES.sm} style={{ color: "#fff" }} />
          <span
            style={{
              fontSize: FONT_SIZES.sm,
              fontWeight: 700,
              color: "#fff",
              fontFamily: FONT_FAMILY,
            }}
          >
            Recent Gifts
          </span>
          {gifts.length > 0 && (
            <span
              style={{
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                padding: "2px 6px",
                borderRadius: 6,
                background: "rgba(255,255,255,0.15)",
                color: "#fff",
                fontFamily: FONT_FAMILY,
              }}
            >
              {gifts.length}
            </span>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 2,
            display: "flex",
            alignItems: "center",
          }}
        >
          <X size={ICON_SIZES.sm} style={{ color: "rgba(255,255,255,0.6)" }} />
        </button>
      </div>

      {/* Subtitle */}
      <div
        style={{
          padding: "8px 16px 4px",
          fontSize: "0.65rem",
          color: palette.text3,
          fontFamily: FONT_FAMILY,
          fontStyle: "italic",
        }}
      >
        Go say thank you.
      </div>

      {/* Donor list */}
      {gifts.length === 0 ? (
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: palette.text3,
            fontSize: FONT_SIZES.xs,
            fontFamily: FONT_FAMILY,
          }}
        >
          No recent gifts.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", padding: "4px 0" }}>
          {gifts.map((gift) => (
            <div
              key={gift.id}
              onClick={() => {
                const fullMember = members.find((m) => m.id === gift.member_id);
                if (fullMember)
                  navigate("memberDetail", fullMember, {
                    from: "recent_gifts",
                    suggestedTemplate: "thank_you",
                  });
              }}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 16px",
                cursor: members.find((m) => m.id === gift.member_id) ? "pointer" : "default",
                transition: "background 0.1s",
              }}
              onMouseEnter={(event) => {
                if (gift.members) event.currentTarget.style.background = `${palette.accent1}08`;
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = "transparent";
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    color: palette.text1,
                    fontFamily: FONT_FAMILY,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {getDonorName(gift)}
                </div>
              </div>
              {cultivationQueue.some((q) => q.memberId === gift.member_id) && (
                <Sprout size={ICON_SIZES.xs} style={{ color: "#10b981", flexShrink: 0 }} title="In cultivation queue" />
              )}
              <span
                style={{
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  color: "#10b981",
                  fontFamily: FONT_FAMILY,
                  flexShrink: 0,
                }}
              >
                {fmtCurrency(gift.amount)}
              </span>
              <span
                style={{
                  fontSize: FONT_SIZES.xs,
                  color: palette.text3,
                  fontFamily: FONT_FAMILY,
                  flexShrink: 0,
                }}
              >
                {fmtDate(gift.gift_date)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
